import { useCallback, useEffect, useRef } from "react";
import { bcp47 } from "../i18n/strings";

/**
 * Speaks translated captions aloud using the browser's native
 * SpeechSynthesis API. This is a deliberate v1 fallback documented in the
 * architecture doc: the backend does not yet stream Azure Neural TTS audio
 * to every listener (see the note in
 * backend/src/translation/azure.provider.ts), so each attendee's own phone
 * synthesizes the caption text locally in their chosen language. This still
 * satisfies "translated voice reaches the listener" end-to-end today, and
 * can be swapped for a real streamed-audio track later without changing the
 * caption pipeline.
 */
const MAX_QUEUED_SENTENCES = 3;

export function useSpeechOut(languageCode: string, enabled: boolean) {
  const queueRef = useRef<string[]>([]);
  const speakingRef = useRef(false);

  const supported = typeof window !== "undefined" && "speechSynthesis" in window;

  const pump = useCallback(() => {
    if (!supported || speakingRef.current) return;
    const next = queueRef.current.shift();
    if (next === undefined) return;
    speakingRef.current = true;
    const utter = new SpeechSynthesisUtterance(next);
    utter.lang = bcp47(languageCode);
    utter.onend = () => {
      speakingRef.current = false;
      pump();
    };
    utter.onerror = () => {
      speakingRef.current = false;
      pump();
    };
    window.speechSynthesis.speak(utter);
  }, [languageCode, supported]);

  const speak = useCallback(
    (text: string) => {
      if (!supported || !enabled || !text.trim()) return;
      queueRef.current.push(text);
      // Speech is slower than reading. If the voice falls behind the speaker,
      // drop the oldest waiting sentences instead of letting the delay grow
      // without bound until the audio is minutes behind the room.
      if (queueRef.current.length > MAX_QUEUED_SENTENCES) {
        queueRef.current.splice(0, queueRef.current.length - MAX_QUEUED_SENTENCES);
      }
      pump();
    },
    [enabled, pump, supported],
  );

  useEffect(() => {
    if (!enabled && supported) {
      queueRef.current = [];
      window.speechSynthesis.cancel();
      speakingRef.current = false;
    }
  }, [enabled, supported]);

  useEffect(() => {
    return () => {
      if (supported) window.speechSynthesis.cancel();
    };
  }, [supported]);

  return { speak, supported };
}
